import { useMemo } from "react";
import { addMonths, addWeeks, differenceInCalendarDays, format, isAfter, parseISO } from "date-fns";
import type { Color } from "@salestrak/ui";
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { useEffectiveScope } from "#app/widgets/dashboards/scope/scope-shared";
import { resolveDatePreset } from "#app/lib/utils/date-presets";
import { resolveWidgetFilters } from "#app/widgets/dashboards/widget-filters";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import {
  timeSeriesGroupBy,
  type TimeSeriesDimension,
  type TimeSeriesGrain,
  type TimeSeriesMeasure,
} from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { UNASSIGNED_DIMENSION_KEY } from "#app/widgets/dashboards/use-dimension-counts";
import { at } from "#app/lib/utils/at";

/** Which timestamp seeds the buckets. */
export type SeriesMeasure = TimeSeriesMeasure;
/** Bucket granularity along the time axis. The chart picks its own width from
 *  the window span, so it never offers the `day` grain the heatmap uses. */
export type SeriesBucket = Exclude<TimeSeriesGrain, "day">;
/** Split the count into one line per scalar dimension, or a single total line. */
export type SeriesGroupBy = TimeSeriesDimension | "none";

/** One point on a line — the bucket start (ms, for stable ordering + the band
 *  x value), its axis label, and the value (per-bucket count or a running total). */
export type LinePoint = { t: number; label: string; y: number };
/** One line — a stage (or the single "Projects" total) and its zero-filled points. */
/** One drawn line: the split entity's id/label, its own palette color (charts
 * wear it; `null` for the total line and unassigned buckets), and the points. */
export type LineSeriesData = { id: string; label: string; color: Color | null; points: LinePoint[] };

/** At most this many stage lines; the rest fold away so the chart stays readable. */
const MAX_SERIES = 8;

/**
 * A time-series reader for the line card. It reuses the SAME server aggregation
 * the count cards use — `useIssueGroupCounts` — asking it to group by a date
 * bucket (`<measure>.<grain>`, or `stage.<measure>.<grain>` for one line per
 * stage) over the shared scope + Filters fold. So the plot is exact per-period
 * counts computed in Postgres, not a client-side sample: it stays accurate no
 * matter how large the table or how lumpy the data (bulk imports included).
 * The returned buckets are zero-filled and continuous between the first and last
 * populated period so a gap reads as zero, not a skipped tick; `cumulative`
 * turns the per-bucket counts into a running total. A card NEVER re-plumbs the
 * scope fold — it calls this.
 */
export function useSeriesLine(
  measure: SeriesMeasure,
  groupBy: SeriesGroupBy,
  cumulative: boolean,
  filters: unknown,
) {
  const { base, override } = resolveWidgetFilters(filters);
  const { effDateRange } = useEffectiveScope(override);
  const bucket = useMemo<SeriesBucket>(() => {
    const { startDate, endDate } = resolveDatePreset(effDateRange.key, effDateRange.custom);
    const spanDays = differenceInCalendarDays(new Date(endDate), new Date(startDate));
    return spanDays <= 92 ? "week" : "month";
  }, [effDateRange.key, effDateRange.custom]);
  const dateField = measure === "updated" ? ("lastUpdateDate" as const) : ("creationDate" as const);
  const { where } = useScopedWhere(base, { dateField, override });
  // Same aggregation the Metric / Pie cards call — just a date-bucket groupBy.
  const groupToken = timeSeriesGroupBy(measure, bucket, groupBy === "none" ? undefined : groupBy);
  const { counts, ready, error } = useIssueGroupCounts({ where, groupBy: groupToken });
  const { stageMap, issueTypeMap, workflowMap } = useWorkspaceData();

  const series = useMemo<LineSeriesData[]>(() => {
    const addFn = bucket === "month" ? addMonths : addWeeks;
    // Labels carry the year so buckets stay unique across years (a year-less
    // "MMM d" collapsed multi-year weeks onto one another). The axis renders a
    // compacted form; this fuller label is the tooltip title.
    const fmt = bucket === "month" ? "MMM yyyy" : "MMM d, yyyy";

    // `counts` keys: total → "YYYY-MM-DD"; split → "<dimensionId|__unassigned__>|YYYY-MM-DD".
    const cells = Object.entries(counts)
      .map(([key, y]) => {
        let seriesId = "__all__";
        let dateStr = key;
        if (groupBy !== "none") {
          const sep = key.lastIndexOf("|");
          seriesId = sep >= 0 ? key.slice(0, sep) : UNASSIGNED_DIMENSION_KEY;
          dateStr = sep >= 0 ? key.slice(sep + 1) : key;
        }
        const date = parseISO(dateStr);
        return Number.isNaN(date.getTime()) ? null : { seriesId, dateStr, date, y };
      })
      .filter((c): c is { seriesId: string; dateStr: string; date: Date; y: number } => c != null);

    if (cells.length === 0) return [];

    // Continuous, zero-filled bucket axis from the earliest to the latest period.
    const firstCell = at(cells, 0);
    let min = firstCell.date;
    let max = firstCell.date;
    for (const c of cells) {
      if (c.date < min) min = c.date;
      if (c.date > max) max = c.date;
    }
    const axis: { key: string; t: number; label: string }[] = [];
    for (let d = min; !isAfter(d, max); d = addFn(d, 1)) {
      axis.push({ key: format(d, "yyyy-MM-dd"), t: d.getTime(), label: format(d, fmt) });
    }
    const indexByKey = new Map(axis.map((b, i) => [b.key, i]));

    const labelOf = (id: string) =>
      groupBy === "stage"
        ? id === UNASSIGNED_DIMENSION_KEY
          ? "No stage"
          : (stageMap.get(id)?.name ?? "Unknown stage")
        : groupBy === "type"
          ? id === UNASSIGNED_DIMENSION_KEY
            ? "No type"
            : (issueTypeMap.get(id)?.name ?? "Unknown type")
          : groupBy === "workflow"
            ? id === UNASSIGNED_DIMENSION_KEY
              ? "No workflow"
              : (workflowMap.get(id)?.name ?? "Unknown workflow")
            : "Projects";

    const colorOf = (id: string): Color | null => {
      if (id === UNASSIGNED_DIMENSION_KEY) return null;
      const raw =
        groupBy === "stage"
          ? stageMap.get(id)?.icon?.color
          : groupBy === "type"
            ? issueTypeMap.get(id)?.icon?.color
            : groupBy === "workflow"
              ? workflowMap.get(id)?.icon?.color
              : null;
      return (raw as Color) ?? null;
    };

    const perSeries = new Map<string, number[]>();
    for (const c of cells) {
      const i = indexByKey.get(c.dateStr);
      if (i == null) continue;
      let arr = perSeries.get(c.seriesId);
      if (!arr) {
        arr = new Array(axis.length).fill(0);
        perSeries.set(c.seriesId, arr);
      }
      // Zero-filled to the axis length, so this slot already holds a number.
      arr[i] = (arr[i] ?? 0) + c.y;
    }

    const built = [...perSeries.entries()].map(([id, perBucket]) => {
      let running = 0;
      const points = axis.map((b, i) => {
        const y = perBucket[i] ?? 0;
        running += y;
        return { t: b.t, label: b.label, y: cumulative ? running : y };
      });
      return { id, label: labelOf(id), color: colorOf(id), points };
    });

    // Busiest lines first; keep the top few so the plot stays legible.
    built.sort(
      (a, b) =>
        b.points.reduce((sum, p) => sum + p.y, 0) - a.points.reduce((sum, p) => sum + p.y, 0),
    );
    return built.slice(0, MAX_SERIES);
  }, [counts, bucket, groupBy, cumulative, stageMap, issueTypeMap, workflowMap]);

  return { series, bucket, loading: !ready, error };
}
